import React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ModalMessage from '../../../UI/organisms/ModalMessage';
import LoadingMessage from '../../../UI/organisms/AppLoadingMessage';
import moneyUtils from '../../../../utils/moneyUtils';
import * as PaymentActions from '../../../../actions/payment';
import { i18n, withTranslation, WithTranslation } from 'next-i18next';
import appUtils from '../../../../utils/appUtils';
import { X } from 'lucide-react';

interface Price {
  stripe_plan_name: string;
  amount: number;
}

interface Plan {
  name: string;
  prices: Price[];
}

interface StripeCard {
  country: string;
}

interface RootState {
  payment: {
    mutatingSubscription: boolean;
  };
}

interface CheckoutReviewModalOwnProps {
  hideModal: () => void;
  isStepModal?: boolean;
  stripeCard?: StripeCard;
  plan: Plan;
  handleSubscribe?: () => void;
  chosenMethod?: string;
  message?: string;
  messageType?: string;
  waitingPaypalToken?: boolean;
  nextStep?: (e?: React.MouseEvent) => void;
}

interface CheckoutReviewModalStateProps {
  waiting?: boolean;
}

interface CheckoutReviewModalDispatchProps {
  paymentActions: Record<string, (...args: unknown[]) => unknown>;
}

type CheckoutReviewModalContentProps = CheckoutReviewModalOwnProps &
  CheckoutReviewModalStateProps &
  CheckoutReviewModalDispatchProps &
  WithTranslation;

function mapStateToProps(state: RootState) {
  const { payment } = state;
  return {
    waiting: payment.mutatingSubscription,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    paymentActions: bindActionCreators(
      PaymentActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ),
  };
}

const CheckoutReviewModalContent = (props: CheckoutReviewModalContentProps) => {
  const {
    t,
    handleSubscribe,
    chosenMethod,
    paymentActions,
    plan,
    waiting,
    hideModal,
    stripeCard,
    message,
    messageType,
    waitingPaypalToken,
  } = props;

  const onClickOk = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentLocale = appUtils.getLocaleFromLang(i18n.language);
    if (chosenMethod === 'Stripe') {
      handleSubscribe();
    } else {
      const currency = moneyUtils.paypalLocaleToCurrency(currentLocale);
      const price = moneyUtils.getPrice(plan.prices, currency);
      const agreementMessage = t(
        'student.billing.checkout.review.transitionToPaypal',
      );
      paymentActions.setupPayPalAgreement(
        price.stripe_plan_name,
        agreementMessage,
      );
    }
  };
  const currentLocale = appUtils.getLocaleFromLang(i18n.language);
  let currency, price: Price | undefined;
  let currencyCode = '';
  if (chosenMethod === 'Stripe') {
    if (stripeCard) {
      currency = moneyUtils.stripeCountryToCurrency(stripeCard.country);
      price = moneyUtils.getPrice(plan.prices, currency);
      currencyCode = moneyUtils.stripeCountryToCurrencyCode(stripeCard.country);
    }
  } else if (chosenMethod === 'PayPal') {
    currency = moneyUtils.paypalLocaleToCurrency(currentLocale);
    price = moneyUtils.getPrice(plan.prices, currency);
    currencyCode = moneyUtils.currencyToCode(currency);
  }
  const amount = price?.amount;
  const wrapperClass = classnames('max-w-lg mx-auto', {
    loading: waiting || waitingPaypalToken,
  });
  const loadingMessage = waiting ? t('general.processing') : null;
  const loadingClass = 'modal-loading-message';

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>
            {t('student.billing.checkout.pickMethod.title')}
          </CardTitle>
          <button type="button" className="modal-close" onClick={hideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <LoadingMessage
            message={loadingMessage}
            loadingClass={loadingClass}
          />
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <div className="mb-4 checkout-review-explanation">
              {t('student.billing.checkout.review.intro', {
                firstCharge: currencyCode + amount,
                monthlyCharge: currencyCode + amount,
              })}
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-9">
                <div className="grid grid-cols-12 gap-2">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.checkout.review.planName')}
                      </strong>
                    </div>
                    <div className="float-right">{plan.name}</div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-12 gap-2">
                  <div className="checkout-review-monthly-charge col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.checkout.review.monthlyCharge')}
                      </strong>
                    </div>
                    <div className="float-right">
                      {currencyCode}
                      {amount}
                    </div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-12 gap-2">
                  <div className="checkout-review-due-today col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.checkout.review.chargeToday')}
                      </strong>
                    </div>
                    <div className="float-right">
                      {currencyCode}
                      {amount}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="control-group text-right">
            <Button
              className="mr-2"
              variant="destructive"
              disabled={waiting}
              onClick={hideModal}
            >
              {t('links.cancel')}
            </Button>
            <Button
              onClick={onClickOk}
              type="submit"
              disabled={waiting || waitingPaypalToken}
            >
              {t('links.subscribe')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(CheckoutReviewModalContent);
export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
